import UIElement from './UIElement';
import SFX from '../media/SFX';
import PageMeta from '../core/PageMeta';

enum UptimeClockMode {
  /**
   * Time since last compile
   */
  Last,

  /**
   * Time since first use (memory loaded)
   */
  Up,

  /**
   * When first brought online (fixed date)
   */
  Begin,

  /**
   * How long since first brought online
   */
  Since,

  /**
   * Until shrug!
   */
  Until,

  /**
   * End element so we can tell when we need to loop
   */
  END,
}

export default class UptimeClock extends UIElement {
  public static readonly Selector: string = '.nav-uptime';

  private static _builtOnTimestamp: number | null = null;

  public static get BuiltOnTimestamp(): number {
    if (!UptimeClock._builtOnTimestamp) {
      const dateStr = PageMeta.get('built-on');
      if (dateStr) {
        UptimeClock._builtOnTimestamp = Date.parse(dateStr);
      } else {
        console.warn('Unable to find built-on meta tag!');
        UptimeClock._builtOnTimestamp = Date.now();
      }
    }
    return UptimeClock._builtOnTimestamp;
  }

  private static _loadedOnTimestamp: number | null = null;

  public static get LoadedOnTimestamp(): number {
    if (!UptimeClock._loadedOnTimestamp) {
      const dateStr = localStorage.getItem('first-loaded-on');
      if (dateStr) {
        UptimeClock._loadedOnTimestamp = Number.parseInt(dateStr, 10);
      } else {
        console.debug('Setting first-loaded-on');
        UptimeClock._loadedOnTimestamp = Date.now();
        localStorage.setItem(
          'first-loaded-on',
          UptimeClock._loadedOnTimestamp.toString(10),
        );
      }
    }
    return UptimeClock._loadedOnTimestamp;
  }

  private static _launchedOnTimestamp: number | null = null;

  public static get LaunchedOnTimestamp(): number {
    if (!UptimeClock._launchedOnTimestamp) {
      const dateStr = PageMeta.get('launched-on');
      if (dateStr) {
        UptimeClock._launchedOnTimestamp = Date.parse(dateStr);
      } else {
        console.warn('Unable to find launched-on meta tag!');
        UptimeClock._launchedOnTimestamp = Date.now();
      }
    }
    return UptimeClock._launchedOnTimestamp;
  }

  public static get LaunchedOnDate(): Date {
    return new Date(UptimeClock.LaunchedOnTimestamp);
  }

  private _mode: UptimeClockMode = UptimeClockMode.Last;

  private get uptimeString(): string {
    switch (this._mode) {
      case UptimeClockMode.Last: {
        const delta = Date.now() - UptimeClock.BuiltOnTimestamp;
        const hours = Math.floor(delta / (1000 * 60 * 60));
        const days = Math.floor(hours / 24);
        return `Last ${days} days, ${hours % 24}hrs`;
      }

      case UptimeClockMode.Up: {
        const delta = Date.now() - UptimeClock.LoadedOnTimestamp;
        const hours = Math.floor(delta / (1000 * 60 * 60));
        const days = Math.floor(hours / 24);
        return `Up ${days} days, ${hours % 24}hrs`;
      }

      case UptimeClockMode.Begin: {
        const launchedDate = UptimeClock.LaunchedOnDate;
        const dayOfMonth = `${launchedDate.getDate()}`.padStart(2, '0');
        const monthOfYear = `${launchedDate.getMonth() + 1}`.padStart(2, '0');
        const year = `${launchedDate.getFullYear()}`;
        const hour = `${launchedDate.getHours()}`.padStart(2, '0');
        const minute = `${launchedDate.getMinutes()}`.padStart(2, '0');
        return `Begin ${dayOfMonth} ${monthOfYear} ${year}, ${hour}:${minute}`;
      }

      case UptimeClockMode.Since: {
        const delta = Date.now() - UptimeClock.LaunchedOnTimestamp;
        const hours = Math.floor(delta / (1000 * 60 * 60));
        const days = Math.floor(hours / 24);
        return `Since ${days} days, ${hours % 24}hrs`;
      }

      case UptimeClockMode.Until:
      default:
        return 'Until  ¯\\_(ツ)_/¯';
    }
  }

  public constructor(element: HTMLElement) {
    super(element);
  }

  public override mount() {
    // Ensure the loaded-on time is initialized when first run even if it's not going to be displayed
    UptimeClock.LoadedOnTimestamp;

    this.updateUptimeString();
    setInterval(this.updateUptimeString.bind(this), 1000);

    this.$element.on('click', async () => {
      this._mode = (this._mode + 1) % UptimeClockMode.END;
      this.updateUptimeString();
      if (this._mode === UptimeClockMode.Until) {
        await SFX.Spoopy.play();
      }
    });
  }

  private updateUptimeString() {
    this.$element.text(this.uptimeString);
  }
}
